/**
 * Per-device "recently OPENED files" for the mobile Recent carousel.
 *
 * The server's `/files/recent` is recently-MODIFIED (`updatedAt`), so merely
 * viewing a file never reorders it (see MobileRecents). This fills that
 * "last opened" gap with a lightweight localStorage store — the Phase-2
 * per-device tracking the mobile plan called for — scoped per identity
 * (tenant + org email) so opens don't bleed across accounts.
 */
export interface RecentOpen {
  id: string
  name: string
  /** Immediate parent folder name — used to disambiguate same-named files. */
  parentName?: string | null
  /** epoch ms when the file was opened on this device */
  at: number
}

const PREFIX = 'papan:mobile:recentOpens:'
const MAX = 30
const EVENT = 'papan:recent-opens-changed'

const keyFor = (scope: string) => `${PREFIX}${scope}`

/**
 * Per-ELEMENT shape guard. `Array.isArray` alone only proves the container:
 * valid-JSON-but-wrong-shape (`["x", 3]`, `[{}]` — a schema change, another
 * tab on an older build, or hand-tampering) would sail through, and an entry
 * with a missing `at` sorts as `NaN` and quietly perturbs the whole order.
 * Malformed entries are dropped rather than thrown on, so a single bad row
 * can't take the carousel down with it.
 */
function isRecentOpen(v: unknown): v is RecentOpen {
  if (typeof v !== 'object' || v === null) return false
  const r = v as Record<string, unknown>
  return typeof r.id === 'string' && r.id !== ''
    && typeof r.name === 'string'
    && typeof r.at === 'number' && Number.isFinite(r.at)
    && (r.parentName == null || typeof r.parentName === 'string')
}

export function readRecentOpens(scope: string): RecentOpen[] {
  try {
    const raw = localStorage.getItem(keyFor(scope))
    const list: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(list) ? list.filter(isRecentOpen) : []
  } catch {
    return []
  }
}

/**
 * Move a file to the front of this device's recent-opens list.
 *
 * The read → prepend → write is NOT atomic: two tabs recording an open at the
 * same moment each read the pre-write list, and the last writer wins — so the
 * other tab's open is lost. Left as-is deliberately. This is a best-effort
 * convenience ordering, the stakes are "a file is missing from a carousel until
 * you open it again", and the alternatives (a lock, or a CRDT-ish merge) cost
 * far more complexity than the bug is worth. Documented so the next reader
 * doesn't mistake it for an oversight.
 */
export function recordFileOpen(scope: string, id: string, name: string, parentName?: string | null): void {
  try {
    const list = readRecentOpens(scope).filter((r) => r.id !== id)
    list.unshift({ id, name, parentName, at: Date.now() })
    localStorage.setItem(keyFor(scope), JSON.stringify(list.slice(0, MAX)))
    // The native `storage` event only fires in OTHER tabs — dispatch our own so
    // the carousel in THIS tab re-sorts immediately.
    window.dispatchEvent(new Event(EVENT))
  } catch {
    /* localStorage blocked (private mode / quota) — recents just won't track */
  }
}

/** Subscribe to changes: same-tab custom event + cross-tab `storage`. */
export function onRecentOpensChanged(cb: () => void): () => void {
  // `storage` fires for EVERY key another tab writes — theme, workspace state,
  // clipboard, anything. Unfiltered, an unrelated cross-tab write re-read
  // localStorage and re-rendered the whole carousel. Only react to OUR keys.
  // (`e.key` is null when a tab calls `localStorage.clear()`; that wipes our
  // keys too, so treat it as relevant.)
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith(PREFIX)) cb()
  }
  window.addEventListener(EVENT, cb)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(EVENT, cb)
    window.removeEventListener('storage', onStorage)
  }
}
